import { Building2, Home, LogOut, Menu, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { DemoUserSwitcher } from "./DemoUserSwitcher";
import { NotificationDropdown } from "./NotificationDropdown";

interface AuthUser {
  email?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  role_name?: string;
}

function displayName(user: AuthUser | null) {
  if (!user) return "";
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "";
}

function initials(user: AuthUser | null) {
  const name = displayName(user);
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

type UserMenuProps = {
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
};

export function UserMenu({ children, align = "end", side = "right" }: UserMenuProps) {
  const navigate = useNavigate();
  const { user, logout, singleTenantMode } = useAuth();
  const typedUser = user as AuthUser | null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="w-56">
        {typedUser ? (
          <>
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium">{typedUser.email}</p>
              <p className="text-xs text-muted-foreground">{typedUser.role_name ?? "Member"}</p>
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem onClick={() => navigate("/user-settings")}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DemoUserSwitcher trigger="menu-item" />
        {/* DESIGN §6A — hide the org switcher for single-tenant-mode
            users (they are locked to one tenant; there is nothing to
            switch to and the route would bounce them back). */}
        {!singleTenantMode ? (
          <>
            <DropdownMenuItem onClick={() => navigate("/select-tenant")}>
              <Building2 className="mr-2 h-4 w-4" />
              Switch Organization
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem
          onClick={() => logout()}
          className="text-destructive focus:text-destructive"
          data-testid="logout-button"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SidebarUserWidget() {
  const { state, isMobile } = useSidebar();
  const { user, isImpersonating } = useAuth();
  const typedUser = user as AuthUser | null;
  const isCollapsed = state === "collapsed" && !isMobile;

  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center gap-2">
        <NotificationDropdown
          triggerClassName="h-9 w-9 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          iconClassName="text-current"
        />
        <UserMenu align="center" side="right">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="User menu"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={typedUser?.avatar_url} />
              <AvatarFallback className="border border-sidebar-border bg-sidebar-accent text-sm font-medium text-sidebar-accent-foreground">
                {initials(typedUser)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </UserMenu>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-sidebar-border bg-sidebar p-2",
        isImpersonating && "border-amber-500/50 bg-amber-500/10",
      )}
    >
      <div className="flex items-center gap-2">
        <UserMenu>
          <Button
            variant="ghost"
            className="h-auto min-w-0 flex-1 justify-start gap-2 rounded-md px-2 py-1.5 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="User menu"
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={typedUser?.avatar_url} />
              <AvatarFallback className="border border-sidebar-border bg-sidebar-accent text-sm font-medium text-sidebar-accent-foreground">
                {initials(typedUser)}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0 text-left">
              <span className="block truncate text-sm font-medium text-sidebar-foreground">
                {displayName(typedUser)}
              </span>
              <span className="block truncate text-xs text-sidebar-foreground/60">
                {typedUser?.role_name ?? "Member"}
              </span>
            </span>
          </Button>
        </UserMenu>
        <NotificationDropdown
          triggerClassName="h-9 w-9 shrink-0 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          iconClassName="text-current"
        />
      </div>
    </div>
  );
}

export function MobileBottomMenu() {
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const typedUser = user as AuthUser | null;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur md:hidden">
      <div className="mx-auto grid max-w-md grid-cols-4 gap-2">
        <button
          type="button"
          onClick={toggleSidebar}
          className="flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
          <span>Menu</span>
        </button>

        <button
          type="button"
          onClick={() => navigate("/home")}
          className="flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
          <span>Home</span>
        </button>

        <NotificationDropdown
          showLabel
          label="Alerts"
          contentAlign="center"
          contentSide="top"
          triggerClassName="flex h-12 w-full flex-col gap-1 rounded-lg bg-transparent px-1 py-0 text-xs font-medium text-muted-foreground shadow-none hover:bg-muted hover:text-foreground"
          iconClassName="h-5 w-5 text-current"
        />

        <UserMenu align="center" side="top">
          <button
            type="button"
            className="flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Open profile"
          >
            <Avatar className="h-5 w-5">
              <AvatarImage src={typedUser?.avatar_url} />
              <AvatarFallback className="bg-muted text-[10px] font-medium text-foreground">
                {initials(typedUser)}
              </AvatarFallback>
            </Avatar>
            <span>Profile</span>
          </button>
        </UserMenu>
      </div>
    </nav>
  );
}
